import type {
  EsnContactChannel,
  EsnFaqEntry,
  EsnSegmentedOption,
} from '@echospaces-web-monorepo/es-ui-next';

/**
 * One Help Center subject (Figma "FAQ Group"): a titled card of up to four questions.
 */
export interface HelpFaqSubject {
  readonly id: string;
  readonly heading: string;
  readonly items: EsnFaqEntry[];
}

/**
 * The portal FAQ, grouped by the four areas of the console (Figma "Help Center", 2587:15814).
 * Answers describe what the portal actually does today; keep them in step with the features.
 */
export const HELP_FAQ: readonly HelpFaqSubject[] = [
  {
    id: 'islands',
    heading: 'Islands',
    items: [
      {
        id: 'island-what',
        question: 'What is an island?',
        answer:
          'An island is one spatial scene — the place your visitors walk into. It holds a hierarchy of slots, ' +
          'and each slot is where a piece of content is shown. Creating one gives you an empty scene in Draft; ' +
          'nothing is visible to anyone until you publish it.',
      },
      {
        id: 'island-states',
        question: 'What is the difference between Draft, Published and Archived?',
        answer:
          'Draft is where you build: only your team sees it. Published is what visitors walk into. Archived takes ' +
          'an island out of service without deleting it — its slots and links stay as they were, so it can be ' +
          'brought back later.',
      },
      {
        id: 'island-versions',
        question: 'Why does my island have more than one version?',
        answer:
          'Each published state of an island is kept as its own version. The live version keeps serving visitors ' +
          'while you prepare the next one, and an earlier version — with the links it had — can be restored if a ' +
          'change does not work out.',
      },
      {
        id: 'island-restore',
        question: 'I deleted an island by mistake. Can I get it back?',
        answer:
          'Archive instead of deleting whenever you are unsure — an archived island can be restored as it was. ' +
          'If an island is already gone, contact support as soon as possible: your company’s data is backed up ' +
          'regularly, and support can tell you what can still be recovered.',
      },
    ],
  },
  {
    id: 'content',
    heading: 'Content',
    items: [
      {
        id: 'content-types',
        question: 'What can I upload?',
        answer:
          'Images, videos, audio and 3D models. One content item can carry a file per language, so the same slot ' +
          'shows each visitor the version in their language.',
      },
      {
        id: 'content-into-island',
        question: 'How does content get into an island?',
        answer:
          'Upload it once in Content, then open Link Content: pick an empty slot of the island on the left, ' +
          'choose the content on the right, and it is placed. The same item can fill slots on as many islands as ' +
          'you like.',
      },
      {
        id: 'content-replace',
        question: 'If I replace a file, what happens to the places it is linked?',
        answer:
          'Uploading a new file to an existing content item adds a new version of it. Every slot linked to that ' +
          'item shows the newest file — there is nothing to re-link.',
      },
      {
        id: 'content-delete-linked',
        question: 'Can I delete content that is still linked?',
        answer:
          'Linked content fills slots in your islands, so removing it leaves those slots empty. Link something ' +
          'else into them first in Link Content — or, if it only needs to look different, replace the file instead ' +
          'of deleting the item.',
      },
    ],
  },
  {
    id: 'accounts',
    heading: 'Accounts and roles',
    items: [
      {
        id: 'roles-what',
        question: 'What can each role do?',
        answer:
          'Every member of your company is Staff and can see its islands and content. Editors can also upload ' +
          'content and link it into islands. Super admins can additionally manage client accounts and open the ' +
          'admin console.',
      },
      {
        id: 'roles-no-accounts',
        question: 'Why can I not see Client accounts?',
        answer:
          'Client accounts and the Admin console are listed only for super admins. If you need them, ask one of ' +
          'your company’s super admins to give you that role.',
      },
      {
        id: 'roles-give-access',
        question: 'How do I give someone access?',
        answer:
          'In Client accounts, add their email address and choose their roles. Someone new gets an email ' +
          'invitation to confirm the address and set a password; someone who already has an EchoSpaces login ' +
          'keeps it and is simply added to your company.',
      },
      {
        id: 'roles-remove-access',
        question: 'How do I take someone’s access away?',
        answer:
          'In Client accounts, remove the member from your company — or take a role away to narrow what they can ' +
          'do. They lose access to your company’s islands and content; their EchoSpaces login itself is not deleted.',
      },
    ],
  },
  {
    id: 'account',
    heading: 'Your account',
    items: [
      {
        id: 'account-name',
        question: 'How do I change my name or the name I sign in with?',
        answer:
          'Open Account Settings from your name at the top of the menu. Your name and sign-in details belong to ' +
          'your EchoSpaces login, so a change there applies wherever you sign in.',
      },
      {
        id: 'account-password',
        question: 'What happens when I change my password?',
        answer:
          'Every other session is signed out — every other device, and any browser you left signed in somewhere ' +
          'else. This one stays. If you cannot remember the current password, support has to reset it for you; ' +
          'there is no self-service reset in the console.',
      },
      {
        id: 'account-theme',
        question: 'Does the console remember my theme?',
        answer:
          'Yes. The light/dark switch at the foot of the menu is remembered in this browser, so the console opens ' +
          'the way you left it.',
      },
      {
        id: 'account-tour',
        question: 'Can I see the introduction again?',
        answer:
          'Yes — Take the tour, directly under Help Center in the menu, starts the walkthrough again at any time.',
      },
    ],
  },
];

/**
 * The Send feedback kinds (Figma "Segmented / Feedback Kind").
 */
export const HELP_FEEDBACK_KINDS: readonly EsnSegmentedOption[] = [
  { value: 'problem', label: 'Problem' },
  { value: 'idea', label: 'Idea' },
  { value: 'question', label: 'Question' },
];

/**
 * Support channels in "Talk to us".
 * TODO(api): placeholder contact data from Figma — replace with the real support desk details.
 */
export const HELP_CONTACT: { readonly hours: string; readonly channels: EsnContactChannel[] } = {
  hours: 'Support answers on weekdays, 09:00–18:00 CET.',
  channels: [
    { label: 'Email', value: 'support@echospaces.de', kind: 'email', note: 'First reply within one working day.' },
    {
      label: 'Phone',
      value: '+49 30 5683 2100',
      kind: 'phone',
      note: 'For a published island that has stopped serving visitors, call rather than write.',
    },
  ],
};

/**
 * The subjects whose question or answer contains every word of `query` (case-insensitive),
 * each narrowed to its matching questions. An empty query returns the whole FAQ.
 */
export function filterHelpFaq(subjects: readonly HelpFaqSubject[], query: string): HelpFaqSubject[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...subjects];

  return subjects
    .map((subject) => ({
      ...subject,
      items: subject.items.filter((item) => {
        const text = `${item.question} ${item.answer}`.toLowerCase();
        return words.every((word) => text.includes(word));
      }),
    }))
    .filter((subject) => subject.items.length > 0);
}

/**
 * How many questions a filtered FAQ still shows.
 */
export function countHelpFaq(subjects: readonly HelpFaqSubject[]): number {
  return subjects.reduce((sum, subject) => sum + subject.items.length, 0);
}
